import { colors, defaultViewPortPadding, presentationDimensions } from '@app/services/export/ppt/variables/constants';
import { gridColWidth } from '@app/services/export/ppt/utils/gridUtils';
import PowerPointPresentation from '@app/services/export/ppt/PowerPointPresentation';
import IPriceTableRow from '@app/types/data/base/IPriceTableRow';
import global from '@app/filters/global';
import { PriceTable } from '@app/services/export/ppt/classes/PriceTable';
import { NumberNavigation, INumberNavigationItem } from '@app/services/export/ppt/classes/NumberNavigation';
import IOfferESlideData from '@app/types/data/slides/IOfferESlideData';
import { createImageForSlide } from '@app/services/export/ppt/utils/layoutUtils';
import PptxGenJS from 'pptxgenjs';
import { Map } from '@app/services/export/ppt/classes/Map';
import { ConnectingTicket } from '@app/services/export/ppt/classes/ConnectingTicket';
import { copy } from '@app/services/export/ppt/variables/typography';
import AbstractOfferSlideProcessor, {
	IPptOfferSlideOptions
} from '@app/services/export/ppt/slides/AbstractOfferSlideProcessor';
import { richTextToPptxText } from '@app/services/export/ppt/utils/formattingUtils';

export default class PowerPointOfferESlideProcessor extends AbstractOfferSlideProcessor<IOfferESlideData> {
	public constructor(slideData: IOfferESlideData, presentation: PowerPointPresentation) {
		const options: Partial<IPptOfferSlideOptions> = {
			omitImage: true,
			imageWidth: defaultViewPortPadding.horizontal + gridColWidth(5)
		};

		super(slideData, presentation, options);
	}

	public async processSlideData(): Promise<void> {
		if (this.slideData.image) {
			await this.adjustImageDimensions(this.slideData.image);
		}

		this.createSlides();
	}

	private createSlides(): void {
		this.slideData.priceMatrix.forEach((item, index) => {
			this.updateSlideData({ visibleSvgGroup: item.svgGroupId });

			const { x, y, w } = this.getAvailableSpaceRectangle();
			const offerSlideBase = this.createOfferSlideBase({
				onBeforeCreate: this.createImageAndMap.bind(this)
			});
			const priceInfoHeight = 0.4;
			const priceInfoMargin = 0.15;

			const dayNavigation = new NumberNavigation(
				offerSlideBase,
				{
					links: this.getDayNavigationData(),
					currentIndex: index,
					singular: this.i18n.day,
					plural: this.i18n.days
				},
				{ x, y, w }
			);

			const priceTable = new PriceTable(
				offerSlideBase,
				{
					rows: this.getTableRowsForIndex(index),
					rowLabelWidth: 1.15,
					rowHeight: 0.47
				},
				{ x, y: dayNavigation.getDimensionRect().bottom + 0.2, w }
			);

			dayNavigation.create();
			priceTable.create();

			if (this.slideData.priceInfo) {
				offerSlideBase.addText(richTextToPptxText(this.slideData.priceInfo), {
					...copy,
					x,
					y: priceTable.getDimensionRect().bottom + priceInfoMargin,
					w,
					h: priceInfoHeight,
					valign: 'top',
					align: 'left',
					color: colors.blue,
					margin: 0
				});
			}

			if (this.slideData.connection) {
				const connectingTicket = new ConnectingTicket(
					offerSlideBase,
					{
						connection: this.slideData.connection,
						seasons: this.slideData.connection.seasons
					},
					{ x, y: priceTable.getDimensionRect().bottom + priceInfoHeight + priceInfoMargin, w }
				);

				connectingTicket.create();
			}
		});
	}

	/**
	 * we have to do it here manually because OfferSlide can only handle one image or map
	 * a slot like implementation is dane here via callback function
	 */
	protected createImageAndMap(slide: PptxGenJS.Slide): void {
		if (this.slideData.image) {
			createImageForSlide(slide, this.slideData.image, {
				x: 0,
				y: 0,
				destinationWidth: this.options.imageWidth,
				destinationHeight: presentationDimensions.h * 0.5,
				type: 'cover'
			});
		}

		const map = new Map(
			slide,
			{
				image: this.slideData.mapImage,
				map: this.slideData.map,
				labels: this.slideData.mapLabels,
				visibleSvgGroup: this.slideData.visibleSvgGroup || 'default'
			},
			{
				x: 0,
				y: presentationDimensions.h * 0.5,
				w: this.options.imageWidth,
				h: presentationDimensions.h * 0.5
			}
		);

		map.create();
	}

	private getDayNavigationData(): INumberNavigationItem[] {
		return this.slideData.priceMatrix.map((item, index) => ({
			numberLabel: item.dayCount,
			slideNumberLink: this.presentation.getSlideNumberById(this.slideData.id) + index
		}));
	}

	private getTableRowsForIndex(index: number): IPriceTableRow[] {
		const i18n: Record<string, string> = this.i18n as Record<string, string>;

		return [
			{
				label: this.i18n.individuals,
				individuals: this.slideData.priceMatrix[index].individuals
			},
			{
				label: this.i18n.groups,
				subline: global.formatI18n(i18n.xPersonsPlus, {
					persons: i18n.minGroupSize
				}),
				groups: this.slideData.priceMatrix[index].groups
			}
		];
	}

	protected getSlideCount(): number {
		return this.slideData.priceMatrix.length;
	}
}
